import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';

import { Table } from '../../../../components/table';
import { getColumns } from './table/getColumns';
import { formatDateApi } from '../../../../components/functions';
import { SelectSingle } from '../../../../components/form';
import { Button } from '../../../../components/custom-essentials';

import {
    fetchMPLessonsSingleND
} from '../../../../actions-nd';
import { updateLearningPlanItems, updateLearningPlan } from '../../../../actions';

const defaultSorted = { dataField: 'combinedSortOrder', order: 'desc' };

function LearningPlanTable(props){
    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => (mounted.current = false);
    });

    const { selectedLearningPlans, studentName, lessons, updateLearningPlanItems, updateLearningPlan, refreshData, hideCompleted } = props;

    const [loading, setLoading] = useState(false);
    const [savingRowIds, setSavingRowIds] = useState({});
    const [draftById, setDraftById] = useState({});
    const saveTimersRef = useRef({});
    const rowByIdRef = useRef({});

    const [addLessonOption, setAddLessonOption] = useState({ value: -1, label: 'Select lesson...' });
    const [isUpdatingPlan, setIsUpdatingPlan] = useState(false);

    const toDateInputValue = useCallback((value) => {
        if (!value) return '';
        if (typeof value === 'string') {
            // Handles 'YYYY-MM-DD' and 'YYYY-MM-DDTHH:mm:ss...'
            if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
        }
        try {
            return formatDateApi(new Date(value));
        } catch {
            return '';
        }
    }, []);

    const getDraftValue = useCallback((row, field) => {
        const rowDraft = draftById[row.id];
        if (rowDraft && Object.prototype.hasOwnProperty.call(rowDraft, field)) {
            return rowDraft[field] ?? '';
        }
        return toDateInputValue(row[field]);
    }, [draftById, toDateInputValue]);

    const setDraftValue = useCallback((row, field, newValue) => {
        setDraftById((prev) => {
            const prevRowDraft = prev[row.id] || {};
            return {
                ...prev,
                [row.id]: {
                    ...prevRowDraft,
                    [field]: newValue
                }
            };
        });
    }, []);

    const revertRowDraft = useCallback((row) => {
        setDraftById((prev) => {
            if (!prev[row.id]) return prev;
            const next = { ...prev };
            delete next[row.id];
            return next;
        });
    }, []);

    const isRowSaving = useCallback((row) => {
        return !!savingRowIds[row.id];
    }, [savingRowIds]);

    const saveRowDatesById = useCallback((rowId) => {
        (async function save() {
            const row = rowByIdRef.current[rowId];
            if (!row) return;

            const rowDraft = draftById[rowId] || {};
            const dateAssigned = rowDraft.date_assigned ?? toDateInputValue(row.date_assigned);
            const dateCompleted = rowDraft.date_completed ?? toDateInputValue(row.date_completed);

            setSavingRowIds((prev) => ({ ...prev, [rowId]: true }));
            const resp = await updateLearningPlanItems({
                learningPlanItems: [{
                    id: row.id,
                    lessonId: row.lesson_id,
                    dateAssigned: dateAssigned ? dateAssigned : null,
                    dateCompleted: dateCompleted ? dateCompleted : null,
                    sortOrder: row.sort_order,
                }]
            });

            const ok = resp && (resp.status === 200 || resp.status === 201);
            if (!ok) {
                console.error('[LPManager] Failed to auto-save learning plan item dates:', resp);
            } else {
                // Clear draft after successful save and refresh list
                revertRowDraft({ id: rowId });
                if (typeof refreshData === 'function') refreshData();
            }

            setSavingRowIds((prev) => {
                const next = { ...prev };
                delete next[rowId];
                return next;
            });
        })();
    }, [draftById, updateLearningPlanItems, toDateInputValue, refreshData, revertRowDraft]);

    const saveRowDatesDirect = useCallback((row, overrides) => {
        (async function save() {
            if (!row) return;
            const rowId = row.id;
            if (savingRowIds[rowId]) return;

            const currentAssigned = toDateInputValue(row.date_assigned);
            const currentCompleted = toDateInputValue(row.date_completed);

            const nextAssigned = Object.prototype.hasOwnProperty.call(overrides || {}, 'date_assigned')
                ? overrides.date_assigned
                : currentAssigned;
            const nextCompleted = Object.prototype.hasOwnProperty.call(overrides || {}, 'date_completed')
                ? overrides.date_completed
                : currentCompleted;

            setSavingRowIds((prev) => ({ ...prev, [rowId]: true }));
            const resp = await updateLearningPlanItems({
                learningPlanItems: [{
                    id: row.id,
                    lessonId: row.lesson_id,
                    dateAssigned: nextAssigned ? nextAssigned : null,
                    dateCompleted: nextCompleted ? nextCompleted : null,
                    sortOrder: row.sort_order,
                }]
            });

            const ok = resp && (resp.status === 200 || resp.status === 201);
            if (!ok) {
                console.error('[LPManager] Failed to save learning plan item dates:', resp);
            } else if (typeof refreshData === 'function') {
                await refreshData();
            }

            setSavingRowIds((prev) => {
                const next = { ...prev };
                delete next[rowId];
                return next;
            });
        })();
    }, [savingRowIds, updateLearningPlanItems, refreshData, toDateInputValue]);

    const scheduleSave = useCallback((rowId, delayMs = 600) => {
        const key = `${rowId}`;
        const existing = saveTimersRef.current[key];
        if (existing) clearTimeout(existing);
        saveTimersRef.current[key] = setTimeout(() => {
            delete saveTimersRef.current[key];
            saveRowDatesById(rowId);
        }, delayMs);
    }, [saveRowDatesById]);

    const onDateChange = useCallback((row, field, value) => {
        setDraftValue(row, field, value);
        scheduleSave(row.id, 600);
    }, [setDraftValue, scheduleSave]);

    const onSetToday = useCallback((row, field) => {
        const today = formatDateApi(new Date());
        setDraftValue(row, field, today);
        saveRowDatesDirect(row, { [field]: today });
    }, [setDraftValue, saveRowDatesDirect]);

    const onClearDate = useCallback((row, field) => {
        setDraftValue(row, field, '');
        saveRowDatesDirect(row, { [field]: null });
    }, [setDraftValue, saveRowDatesDirect]);

    const downloadFile = useCallback((fileInfo, isReview, appendLogo) => {
        const squareColor = { r: 0, g: 0, b: 0 };
        if(fileInfo.type === 'epLesson' || isReview){
            squareColor.g = 122;
            squareColor.b = 157;
        } else if(fileInfo.type === 'lesson'){
            squareColor.r = 215;
        }

        fileInfo.options = {
            studentName,
            appendName: studentName && appendLogo ? true : false,
            appendLogo,
            appendSquare: appendLogo,
            squareColor
        };
        (async function doDownload(){
            if(loading) return;
            if(mounted.current) setLoading(true);
            await fetchMPLessonsSingleND(fileInfo);
            if(mounted.current) setLoading(false);
        })();
    }, [loading, setLoading, studentName]);
    const columns = useMemo(() => {
        return getColumns(downloadFile, {
            getDraftValue,
            setDraftValue,
            onDateChange,
            onSetToday,
            onClearDate,
            isRowSaving,
            canRemoveLessons: selectedLearningPlans.length === 1,
            onRemoveLesson: (row) => null, // wired below when single LP selected
        });
    }, [downloadFile, getDraftValue, setDraftValue, onDateChange, onSetToday, onClearDate, isRowSaving, selectedLearningPlans.length]);
    const learningPlanItems = useMemo(() => {
        const lpItems = [];
        let lpIndex = 1;
        selectedLearningPlans.forEach(lp => {
            const items = lp.obj?.items || [];
            items.forEach(i => {
                i.combinedSortOrder = `${lpIndex}_${i.sort_order}`;
                lpItems.push(i);
            });
            lpIndex++;
        });
        if (hideCompleted) {
            return lpItems.filter((row) => !toDateInputValue(row.date_completed));
        }
        return lpItems;
    },[selectedLearningPlans, hideCompleted, toDateInputValue]);

    // Maintain a lookup map for autosave
    useEffect(() => {
        const map = {};
        learningPlanItems.forEach((row) => {
            map[row.id] = row;
        });
        rowByIdRef.current = map;
    }, [learningPlanItems]);

    const isSingleLpSelected = selectedLearningPlans.length === 1;
    const selectedLpObj = isSingleLpSelected ? selectedLearningPlans[0]?.obj : null;

    const buildLearningPlanParams = useCallback((lp) => {
        if (!lp) return null;
        return {
            id: lp.id,
            assessmentId: lp.assessment_id,
            name: lp.name,
            student: lp.student,
            notes: lp.notes || '',
            isReview: parseInt(lp.is_review) === 1 ? 1 : 0,
            sortOrder: lp.sort_order,
            active: parseInt(lp.active) === 1 ? 1 : 0,
        };
    }, []);

    const buildLearningPlanItemsParams = useCallback((items) => {
        // Backend contract: PUT /mp/learning-plans expects `learningPlanItems` as a top-level param,
        // and replaces ALL existing items (delete + insert). So for add/remove we submit the full list.
        const sorted = [...(items || [])].sort((a, b) => parseInt(a.sort_order) - parseInt(b.sort_order));
        return sorted.map((row, idx) => ({
            lessonId: parseInt(row.lesson_id),
            dateAssigned: toDateInputValue(row.date_assigned) || null,
            dateCompleted: toDateInputValue(row.date_completed) || null,
            sortOrder: idx + 1,
        }));
    }, [toDateInputValue]);

    const updatePlanItems = useCallback(async (nextItems) => {
        if (!isSingleLpSelected || !selectedLpObj) return;
        if (isUpdatingPlan) return;

        const learningPlan = buildLearningPlanParams(selectedLpObj);
        if (!learningPlan) return;

        const learningPlanItemsParams = buildLearningPlanItemsParams(nextItems);

        setIsUpdatingPlan(true);
        const resp = await updateLearningPlan({
            learningPlan,
            learningPlanItems: learningPlanItemsParams,
        });
        const ok = resp && (resp.status === 200 || resp.status === 201);
        if (!ok) {
            console.error('[LPManager] Failed to update learning plan (add/remove lesson):', resp);
        } else if (typeof refreshData === 'function') {
            await refreshData();
        }
        setIsUpdatingPlan(false);
    }, [isSingleLpSelected, selectedLpObj, isUpdatingPlan, buildLearningPlanParams, buildLearningPlanItemsParams, updateLearningPlan, refreshData]);

    const lessonOptions = useMemo(() => {
        if (!isSingleLpSelected || !Array.isArray(lessons)) return [{ value: -1, label: 'Select lesson...' }];

        const existingLessonIds = new Set((selectedLpObj?.items || []).map((i) => parseInt(i.lesson_id)));
        const opts = lessons
            .filter((l) => parseInt(l.active) === 1)
            .filter((l) => !existingLessonIds.has(parseInt(l.id)))
            .map((l) => ({ value: parseInt(l.id), label: `${l.lesson_id}: ${l.name}`, obj: l }));

        return [{ value: -1, label: 'Select lesson...' }, ...opts];
    }, [isSingleLpSelected, lessons, selectedLpObj]);

    const onAddLesson = useCallback(async () => {
        if (!isSingleLpSelected || !selectedLpObj) return;
        if (!addLessonOption || parseInt(addLessonOption.value) === -1) return;

        const nextItems = [...(selectedLpObj.items || [])];
        nextItems.push({
            id: `new-${addLessonOption.value}-${Date.now()}`,
            lesson_id: parseInt(addLessonOption.value),
            date_assigned: null,
            date_completed: null,
            sort_order: (nextItems.length ? Math.max(...nextItems.map(i => parseInt(i.sort_order) || 0)) : 0) + 1,
            lessonObj: addLessonOption.obj || {}
        });

        await updatePlanItems(nextItems);
        setAddLessonOption({ value: -1, label: 'Select lesson...' });
    }, [isSingleLpSelected, selectedLpObj, addLessonOption, updatePlanItems]);

    const onRemoveLesson = useCallback(async (row) => {
        if (!isSingleLpSelected || !selectedLpObj) return;
        const rowId = row?.id;
        const nextItems = (selectedLpObj.items || []).filter((i) => i.id !== rowId);
        await updatePlanItems(nextItems);
    }, [isSingleLpSelected, selectedLpObj, updatePlanItems]);

    const columnsWithRemoveWired = useMemo(() => {
        if (!isSingleLpSelected) return columns;
        // Replace placeholder onRemoveLesson by regenerating columns with correct handler
        return getColumns(downloadFile, {
            getDraftValue,
            setDraftValue,
            onDateChange,
            onSetToday,
            onClearDate,
            isRowSaving,
            canRemoveLessons: true,
            onRemoveLesson,
        });
    }, [isSingleLpSelected, columns, downloadFile, getDraftValue, setDraftValue, onDateChange, onSetToday, onClearDate, isRowSaving, onRemoveLesson]);

    return (
        <div>
            {isSingleLpSelected ? (
                <div className="flex flex-row items-end gap-x-3 mb-4">
                    <div className="w-1/2">
                        <SelectSingle
                            id="lp-manager-add-lesson"
                            label="Add Lesson"
                            name="addLesson"
                            value={addLessonOption}
                            options={lessonOptions}
                            onChange={(e) => setAddLessonOption(e.target.value)}
                            disabled={isUpdatingPlan}
                        />
                    </div>
                    <div className="pb-1">
                        <Button
                            color="lte-mpEGreen"
                            disabled={isUpdatingPlan || !addLessonOption || parseInt(addLessonOption.value) === -1}
                            onClick={onAddLesson}
                        >
                            Add Lesson
                        </Button>
                    </div>
                </div>
            ) : null}
            <Table
                tableName="learningPlanItems"
                data={learningPlanItems}
                columns={columnsWithRemoveWired}
                defaultSorted={defaultSorted}
                sizePerPageInitial={50}
                emptyMessage="No items were found"
            />
        </div>
    );
}

export default connect(null, {
    updateLearningPlanItems,
    updateLearningPlan,
})(LearningPlanTable);